/**
 * 一覧の絞り込みタブ（案件・見積書・請求書の一覧。2026-09-24 射場さん「絞り込みのボタンをタブ形式に。タブの幅は均等に」）。
 *   ・見た目は案件の画面のタブ（.gtab）と同じ。幅だけ全部そろえる。9/29（#94）から、その一覧でいちばん長い「名前＋件数」に合わせる
 *     （CSS の grid の 1fr の列。固定の幅はやめた）
 *   ・9/29（#91）：文字だけのタブ。選んだタブは藍＋太字、件数は丸いしるし、下線 3px（.ind）が 220ms ですべる。表はその下のカード
 *   ・side（担当・探す）がタブと1段に入らないときは、タブの上の段へ（#94。下の段に落とさない＝下線がいつも帯の線に接する）
 *   ・幅が足りないときは枠の幅で形を変える（2行の文字・6つなら3つずつ2段・7つなら4つと3つの2段。styles.css の .ltabwrap）。横に隠れるタブを作らない。
 *     2段に折れたときは .ind を隠し、選んだタブ自身に下線を引く
 *   ・それでも1行の形で名前が「…」になる・はみ出すときは、測って2行の文字にする（#95、useFold。フォントに左右されない）
 *   ・キーボード：← → Home End で移って選ぶ（案件の画面のタブと同じ）
 *   ・一覧の頭（#241、docs/94 B-3）：filter・create を渡すと タブ（左）｜絞り込み（残りの幅の中央）｜主ボタン（右端）の1段。
 *     1段に入らないときは、絞り込みを一覧の頭（ListHead）の題の行へ移す（ListHead の中でないとき＝{project}の画面のタブの中は、今までどおりタブの上の段へ）
 */
import { useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useListHeadSlot } from "./ListHead.jsx";

/**
 * タブの下線（.ind）を、いま選んでいるタブ（role="tab" aria-selected="true"）の下に置く。
 * 枠の大きさが変わったら置き直す（ResizeObserver）。
 * すべらせるのは選んだタブが変わったとき（とその動きのあいだ）だけ。最初の1回と、件数が入る・枠の幅が変わるなどで
 * タブの幅や位置が変わっただけのときは、すべらせずに置く（#94：タブの幅が中身で決まるようになり、件数が入るたびに下線が伸び縮みして見えた）
 */
export function useTabIndicator(box: RefObject<HTMLElement | null>, ind: RefObject<HTMLElement | null>, key: unknown) {
  const shown = useRef<HTMLElement | null>(null);
  /** この時刻まではすべらせる（選んだ直後に件数が入って置き直しても、動きを途中で切らない。動きは 220ms） */
  const slideUntil = useRef(0);
  useLayoutEffect(() => {
    const bar = box.current;
    const line = ind.current;
    if (!bar || !line) return;
    const place = () => {
      const t = bar.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      if (!t) {
        line.style.width = "0px";
        shown.current = null;
        return;
      }
      const now = performance.now();
      if (shown.current !== null && shown.current !== t) slideUntil.current = now + 250;
      shown.current = t;
      const slide = now < slideUntil.current;
      if (!slide) line.style.transition = "none";
      line.style.width = `${t.offsetWidth}px`;
      line.style.transform = `translateX(${t.offsetLeft}px)`;
      if (!slide) {
        void line.offsetWidth; // すべらせずに置いてから、次から動きを戻す
        line.style.transition = "";
      }
    };
    place();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
    ro?.observe(bar);
    return () => ro?.disconnect();
  }, [box, ind, key]);
}

/**
 * 1行の形でタブの名前が「…」になる・帯の中で横にはみ出すなら、帯（.ltabbar）に data-fold を付けて2行の文字にする（#95）。
 * 本番のフォント（BIZ UDPゴシック）なら CSS の枠 1040px で足りるが、フォントが読めずに代わりのフォントになったとき（括弧が全角で広い）や
 * 件数が4けたのときも「…」にしない。いつも「外して1行の形で測る → 付け直す」を同じ流れの中でするので、途中の形は絵に出ない。
 * 枠の幅は高さ 0 の目印（.ltab-sense）で見る（2行にしても目印の大きさは変わらない＝ResizeObserver が回り続けない）。
 * Web フォント（BIZ UDPゴシック）の読み込みが終わると字の幅が変わるので、そのときも測り直す（#96：document.fonts.ready と loadingdone）。
 * はみ出しの判定に選んだタブの下線（.ind。absolute で帯の scrollWidth に入る）を数えない（#233）。下線は前の形の位置のまま残っているので、
 * 右端のタブを選んでいると件数が「…」から数字に変わって帯が縮んだだけで「はみ出す」と出て、2行の形のまま戻らなかった（2行の形では下線がさらに右へ置かれる）。
 * 下線を隠して測ると、すべっている途中の動きが切れる＝下線にはさわらず、帯の scrollWidth がはみ出すと言うときだけ、タブとその中身の右端（offset＝帯からの配置。
 * 横に流した量や transform に左右されない）で確かめ直す
 * data-fold は React が持たない属性（className を書き直されても消えない）
 */
function useFold(sense: RefObject<HTMLElement | null>, bar: RefObject<HTMLElement | null>, list: RefObject<HTMLElement | null>, key: unknown) {
  useLayoutEffect(() => {
    const s = sense.current;
    const b = bar.current;
    const tl = list.current;
    if (!s || !b || !tl) return;
    /** タブと、タブの中の名前・件数の右端（下線は数えない。帯は position:relative なので offset は帯から測った値） */
    const contentRight = () => Math.max(0, ...[...tl.querySelectorAll<HTMLElement>(".ltab, .ltab > *")].map((e) => e.offsetLeft + e.offsetWidth));
    const decide = () => {
      b.removeAttribute("data-fold");
      const cut =
        // offset は整数に丸めた値（タブの幅が小数のとき右端が 1px はみ出して見える＝#241 の 15px のタブで件数が「…」→「0」に変わった時に誤って2行の形になった）＝1px までは入っているとみなす
        (tl.scrollWidth > tl.clientWidth + 1 && contentRight() > tl.clientWidth + 1) || [...tl.querySelectorAll<HTMLElement>(".ltab-l")].some((l) => l.scrollWidth > l.clientWidth);
      b.toggleAttribute("data-fold", cut);
    };
    decide();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(decide);
    ro?.observe(s);
    // Web フォントが読み終わったら測り直す（枠の幅は変わらないので ResizeObserver では気づけない）
    let live = true;
    const fonts = typeof document !== "undefined" ? document.fonts : undefined;
    const again = () => {
      if (live) decide();
    };
    void fonts?.ready.then(again);
    fonts?.addEventListener("loadingdone", again);
    return () => {
      live = false;
      ro?.disconnect();
      fonts?.removeEventListener("loadingdone", again);
    };
  }, [sense, bar, list, key]);
}

/**
 * 一覧の型の表の見出しを画面の上に貼り付ける（#302 A5、docs/94 B-3 の追記）。表のカード（.lcard）の中の表の枠（タブの下の tabpanel）が
 * 横に流れないとき（表が枠に入るとき）だけ data-fit を付ける＝CSS が枠を横に流す入れ物にしない（overflow-x:clip）ので、見出しは上の帯の下に貼り付く。
 * 表が枠より広いとき（狭い幅）は今までどおり横に流す（見出しは枠の上のまま）。表が入れ替わる（タブ・ゴミ箱）・幅が変わるたびに測り直す
 */
function useStickyHead(id: string, key: unknown) {
  useLayoutEffect(() => {
    const panel = document.getElementById(`${id}-panel`);
    if (!panel || !panel.closest(".lcard") || !panel.classList.contains("tbl-wrap")) return;
    let table: HTMLTableElement | null = null;
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => decide());
    const decide = () => {
      const t = panel.querySelector<HTMLTableElement>(":scope > table.list");
      if (t !== table) {
        if (table) ro?.unobserve(table);
        table = t;
        if (t) ro?.observe(t);
      }
      panel.toggleAttribute("data-fit", !!t && t.offsetWidth <= panel.clientWidth + 1);
    };
    decide();
    ro?.observe(panel);
    const mo = typeof MutationObserver === "undefined" ? null : new MutationObserver(decide);
    mo?.observe(panel, { childList: true });
    return () => {
      ro?.disconnect();
      mo?.disconnect();
      panel.removeAttribute("data-fit");
    };
  }, [id, key]);
}

export type ListTab<K extends string> = { key: K; label: string; count?: number | string | undefined };

/** タブ1つの中身の幅（左右の余白＋名前＋あいだ＋件数のしるし）のいちばん大きいもの。「…」になっていても名前の全部の幅で測る */
function naturalTabWidth(tl: HTMLElement): number {
  return Math.max(
    0,
    ...[...tl.querySelectorAll<HTMLElement>(".ltab")].map((t) => {
      const cs = getComputedStyle(t);
      const l = t.querySelector<HTMLElement>(".ltab-l");
      const n = t.querySelector<HTMLElement>(".num");
      return parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) + (l?.scrollWidth ?? 0) + (n ? (parseFloat(cs.columnGap) || 0) + n.getBoundingClientRect().width : 0);
    }),
  );
}

export function ListTabs<K extends string>({
  id,
  label,
  tabs,
  value,
  onChange,
  side,
  filter,
  filterMin,
  create,
}: {
  /** タブと表の枠の id の頭（ページの中で1つ） */
  id: string;
  /** 読み上げ用のタブ全体の名前 */
  label: string;
  tabs: readonly ListTab<K>[];
  value: K;
  onChange: (k: K) => void;
  /** タブの右に置くもの（案件の画面のタブの中の作成のボタンなど）。入らないときはタブの上の段へ */
  side?: ReactNode;
  /** 一覧の絞り込みの欄（FilterInput。#241）：タブの右の残りの幅の中央。1段に入らないときは一覧の頭の題の行へ */
  filter?: ReactNode;
  /** 絞り込みに取る幅の下限（入るかを測るとき。既定 240px） */
  filterMin?: number;
  /** 主ボタン（＋ ○○作成。#241）：右端 */
  create?: ReactNode;
}) {
  const tabsRef = useRef<HTMLDivElement>(null);
  const indRef = useRef<HTMLSpanElement>(null);
  const senseRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const createRef = useRef<HTMLSpanElement>(null);
  const filterRef = useRef<HTMLSpanElement>(null);
  const slot = useListHeadSlot();
  const [up, setUp] = useState(false);
  const shape = `${value}|${tabs.map((t) => `${t.label}${t.count ?? ""}`).join(",")}`;
  const hasFilter = filter != null && filter !== false;
  // 絞り込みが1段に入るか（タブの中身の幅×数＋絞り込みの下限＋主ボタン）。入らなければ題の行へ。打っている最中は動かさない（欄を作り直すとフォーカスが外れる）
  useLayoutEffect(() => {
    const b = barRef.current;
    const tl = tabsRef.current;
    if (!b || !tl || !hasFilter || !slot) {
      setUp(false);
      return;
    }
    const decide = () => {
      const typing = document.activeElement instanceof HTMLElement && (filterRef.current?.contains(document.activeElement) || slot.contains(document.activeElement));
      if (typing) return;
      const ts = tl.querySelectorAll<HTMLElement>(".ltab");
      const tabsW = Math.ceil(naturalTabWidth(tl)) * ts.length + (parseFloat(getComputedStyle(tl).columnGap) || 0) * Math.max(0, ts.length - 1);
      const gap = parseFloat(getComputedStyle(b).columnGap) || 0;
      const need = tabsW + gap + (filterMin ?? 240) + gap + (createRef.current?.offsetWidth ?? 0);
      setUp(need > b.clientWidth);
    };
    decide();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(decide);
    if (senseRef.current) ro?.observe(senseRef.current);
    let live = true;
    void document.fonts?.ready.then(() => live && decide());
    return () => {
      live = false;
      ro?.disconnect();
    };
  }, [shape, slot, hasFilter, filterMin]);
  // 表のカードの中（一覧の頭）：タブの幅を整数の px に決める（いちばん長いタブ。1fr の小数の幅だと右端が 1px はみ出して見える）
  useLayoutEffect(() => {
    const tl = tabsRef.current;
    if (!tl || !tl.closest(".lcard")) return;
    const set = () => tl.style.setProperty("--ltab-w", `${Math.ceil(naturalTabWidth(tl))}px`);
    set();
    let live = true;
    void document.fonts?.ready.then(() => live && set());
    return () => {
      live = false;
    };
  }, [shape]);
  useStickyHead(id, shape);
  useFold(senseRef, barRef, tabsRef, `${shape}|${up}`); // 形を決めてから下線を置く
  useTabIndicator(tabsRef, indRef, `${shape}|${up}`);
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const n = tabs.length;
    const j = e.key === "ArrowRight" ? (i + 1) % n : e.key === "ArrowLeft" ? (i - 1 + n) % n : e.key === "Home" ? 0 : e.key === "End" ? n - 1 : -1;
    const t = tabs[j];
    if (!t) return;
    e.preventDefault();
    onChange(t.key);
    document.getElementById(`${id}-tab-${t.key}`)?.focus();
  };
  return (
    <div className="ltabwrap">
      <div className="ltab-sense" ref={senseRef} aria-hidden="true" />
      {/* --ltab-c：枠 700px 以下で何列に並べるか（6つまでは3つずつ2段。7つ以上は2段に入るよう半分ずつ＝見積書の一覧の7つ（#200 でゴミ箱）は4・3） */}
      <div className={"ltabbar" + (tabs.length > 4 ? " many" : "")} style={{ "--ltab-n": tabs.length, "--ltab-c": tabs.length > 6 ? Math.ceil(tabs.length / 2) : 3 } as CSSProperties} ref={barRef}>
        <div className="ltabs" role="tablist" aria-label={label} ref={tabsRef}>
          {tabs.map((t, i) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              id={`${id}-tab-${t.key}`}
              aria-controls={`${id}-panel`}
              aria-selected={value === t.key}
              tabIndex={value === t.key ? 0 : -1}
              className={"ltab" + (value === t.key ? " on" : "")}
              onClick={() => onChange(t.key)}
              onKeyDown={(e) => onKey(e, i)}
            >
              <span className="ltab-l">{t.label}</span>
              {t.count != null ? (
                <>
                  {" "}
                  <span className="num">{t.count}</span>
                </>
              ) : null}
            </button>
          ))}
          <span className="ind" ref={indRef} aria-hidden="true" />
        </div>
        {hasFilter || create ? (
          <div className="ltabs-side">
            {hasFilter && !(up && slot) ? (
              <span className="lbar-filter" ref={filterRef}>
                {filter}
              </span>
            ) : (
              <span className="sp" />
            )}
            {side}
            {create ? (
              <span className="lbar-create" ref={createRef}>
                {create}
              </span>
            ) : null}
          </div>
        ) : side ? (
          <div className="ltabs-side">{side}</div>
        ) : null}
      </div>
      {hasFilter && up && slot ? createPortal(<span ref={filterRef}>{filter}</span>, slot) : null}
    </div>
  );
}

/** タブの下の表の枠に付ける（role="tabpanel"。いま選んでいるタブを名前にする） */
export function listTabPanel(id: string, value: string) {
  return { role: "tabpanel", id: `${id}-panel`, "aria-labelledby": `${id}-tab-${value}` } as const;
}
